import { FC } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { selectedUnit } from '../../redux/reducers/unitSlice';
import {
  resolveUnitBaseColor,
  unitAccentTextColor,
} from '../../businessLogicService/StudentDashboardAux/getColorByUnit';

interface Props {
  // Short label of the unit-path segment currently in view: "Foundations" | "Target N".
  label: string;
  // True when the unlocked frontier has reached this segment, i.e. it holds connectors the student can
  // already work on, so it takes the unit's accent color. Segments beyond the frontier — still locked,
  // scrolled to out of curiosity — read in neutral.
  isReached: boolean;
}

// Sized to the longest label ("Foundations") so the strip and its end node keep the exact same width
// and position as the student scrolls from one segment to the next.
const STRIP_WIDTH_CLASS_NAME = 'tw-w-[172px]';

/**
 * The unit-path segment the student is currently scrolled to, drawn as the paper strip at the bottom of
 * UnitPathRail so they always know where they are in the unit — even with the header collapsed. The
 * right end is fully rounded so the strip reads as a tape closed by its end node.
 * The label is unit-colored while the segment is inside the open part of the path and neutral past the
 * unlocked frontier, so the color doubles as "this is still reachable".
 * Announced politely so screen readers report the segment change without interrupting.
 */
export const UnitPathSegmentLabel: FC<Props> = ({ label, isReached }) => {
  const unit = useAppSelector(selectedUnit);
  const textColorClassName = isReached ? unitAccentTextColor[resolveUnitBaseColor(unit?.name)] : 'tw-text-neutral-600';

  return (
    <div
      aria-live="polite"
      className={`tw-relative tw-flex tw-h-8 ${STRIP_WIDTH_CLASS_NAME} tw-items-center tw-rounded-l-sm tw-rounded-r-full tw-bg-stone-100 tw-pl-3 tw-pr-10 tw-shadow-sm`}
    >
      <span className={`tw-font-atkinson tw-text-sm tw-font-bold tw-uppercase tw-tracking-wide ${textColorClassName}`}>
        {label}
      </span>
      {/* Decorative end node closing the strip, matching the round connector ends of the unit path. */}
      <span
        aria-hidden="true"
        className="tw-absolute tw-right-1 tw-h-6 tw-w-6 tw-rounded-full tw-bg-yellow-300 tw-shadow-sm"
      />
    </div>
  );
};

export default UnitPathSegmentLabel;
